// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getAlbumDetail, getGcloudAll, getLocalAndroidAll } from 'src/@api/dashboard'
import { addDeviceSwitchReset } from 'src/store/apps/deviceSwitchHandlers'
import { selectDeviceRequestKey } from 'src/store/apps/Common'

const createInitialState = () => ({
  foldersInfos: [],
  fileRid: '',
  level: 0,
  selectedData: [],
  allData: [],
  useImages: [],
  nowName: '',
  isLoading: false,
  deviceRequestKey: '',
  activePhotoRequestId: ''
})

/** 统一相册标识（All Photos 可能只有 name，无 album 字段） */
export const getAlbumKey = row => row.album || row.name || row.id

/** 格式化视频时长（秒 → mm:ss） */
const formateSeconds = endTime => {
  let secondTime = parseInt(endTime)
  let min = 0
  let h = 0
  let result = ''
  if (secondTime > 60) {
    min = parseInt(secondTime / 60)
    secondTime = parseInt(secondTime % 60)
    if (min > 60) {
      h = parseInt(min / 60)
      min = parseInt(min % 60)
    }
  }
  result = `${min.toString().padStart(2, '0')}:${secondTime.toString().padStart(2, '0')}`

  return result
}

/** 追加数据（相同相册合并，不同相册追加新对象） */
const appendChildData = (oldData, row, result) => {
  const newData = []
  const albumKey = getAlbumKey(row)

  if (oldData.length === 0) {
    newData.push({ name: albumKey, list: [...result] })
  } else {
    oldData.forEach(item => {
      if (item.name === albumKey) newData.push({ name: item.name, list: [...item.list, ...result] })
      else newData.push(item)
    })

    const flag = newData.some(item => item.name === albumKey)
    if (!flag) newData.push({ name: albumKey, list: [...result] })
  }

  return newData
}

const mapAlbumContents = (contents, type) =>
  contents.map(item => {
    if (type === 'android-local') {
      if (item.type == 'mp4' || item.type == 'mov' || item.type == 'video') {
        return {
          ...item,
          original_url: item.original?.url || '',
          width: 255,
          type: 'mp4',
          cover_url: item.cover?.url || '',
          cover_duration: item.cover?.duration != null ? formateSeconds(item.cover.duration) : '',
          play: false
        }
      }

      return {
        ...item,
        original_url: item.original.url,
        medium_url: item.medium.url,
        thumb_url: item.thumb.url,
        width: item.thumb.width || 255,
        type: item.type == 'mp4' || item.type == 'mov' || item.type == 'video' ? 'mp4' : item.type,
        cover_url: item.type == 'mp4' || item.type == 'mov' || item.type == 'video' ? item.cover.url : '',
        cover_duration:
          item.type == 'mp4' || item.type == 'mov' || item.type == 'video'
            ? formateSeconds(item.cover.duration)
            : '',
        play: false
      }
    }

    const thumbType = item.thumb?.type
    const isVideo = thumbType == 'mp4' || thumbType == 'mov' || thumbType == 'video'

    return {
      ...item,
      original_url: item.original?.url,
      medium_url: item.medium?.url,
      thumb_url: item.thumb?.url,
      width: item.thumb?.width,
      type: isVideo ? 'mp4' : thumbType,
      cover_url: isVideo ? item.cover?.url : '',
      cover_duration: isVideo ? formateSeconds(item.cover?.duration) : ''
    }
  })

/**
 * 拉取相册详情页数据；分页状态全部来自 Redux，不再使用模块级 let。
 * @param {Object} args
 * @param {string} args.type
 * @param {Object} args.row
 * @param {number} args.reqNum
 * @param {string} args.getDeviceUser
 * @param {Object} args.userData
 * @param {string} args.requestKey
 */
const fetchPhotoDataRequest = createAsyncThunk(
  'fetchPhotoData',
  async ({ type, row, reqNum, getDeviceUser, userData, requestKey }, { getState }) => {
    const photo = getState().Photo

    // 从 Redux 拷贝工作副本，避免模块级可变变量跨设备泄漏
    let foldersInfos = (photo.foldersInfos || []).map(item => ({ ...item }))
    let selectedData = (photo.selectedData || []).map(item => ({
      ...item,
      list: [...(item.list || [])]
    }))
    let allData = (photo.allData || []).map(item => ({
      ...item,
      list: [...(item.list || [])]
    }))
    let useImages = (photo.useImages || []).map(item => ({
      ...item,
      list: [...(item.list || [])]
    }))
    let fileRid = photo.fileRid || ''
    let level = photo.level || 0
    let nowOffset = 0

    const albumKey = getAlbumKey(row)

    if (foldersInfos.length === 0) {
      foldersInfos.push({ name: albumKey, offset: 0 })
    } else {
      const newOffset = []

      foldersInfos.forEach(item => {
        if (item.name === albumKey) {
          nowOffset = Number(item.offset) + Number(reqNum)
          newOffset.push({ ...item, offset: nowOffset })
        } else {
          newOffset.push(item)
        }
      })

      const flag = newOffset.some(item => item.name === albumKey)
      if (!flag) newOffset.push({ name: albumKey, offset: 0 })

      foldersInfos = [...newOffset]
    }

    const now = foldersInfos.find(item => item.name === albumKey)
    const maxNum = now !== undefined ? now.maxNum : undefined
    const nowName = albumKey

    if (maxNum !== undefined) {
      return {
        foldersInfos,
        fileRid,
        level,
        selectedData,
        allData,
        useImages,
        nowName,
        requestKey
      }
    }

    let response
    if (type === 'android-local') {
      fileRid = row.name
      response = await getLocalAndroidAll({
        username: userData.email,
        params: {
          category: 'ALBUM_DETAIL',
          deviceUdid: getDeviceUser,
          offset: nowOffset,
          limit: reqNum,
          rid: row.name
        }
      })
    } else if (type === 'google-cloud') {
      fileRid = row.name
      response = await getGcloudAll({
        username: getDeviceUser,
        params: {
          category: 'ALBUM_DETAIL',
          offset: nowOffset,
          limit: reqNum,
          rid: row.id
        }
      })
    } else {
      fileRid = albumKey
      response = await getAlbumDetail({
        username: getDeviceUser,
        params: {
          category: 'ALBUM_DETAIL',
          offset: nowOffset,
          limit: reqNum,
          rid: albumKey
        }
      })
    }

    level = 1

    if (response.data.contents === null) {
      foldersInfos.forEach((item, index) => {
        if (item.name === albumKey) foldersInfos[index] = { ...item, maxNum: true }
      })

      selectedData = appendChildData(selectedData, row, [])
      allData = selectedData

      return {
        foldersInfos,
        fileRid,
        level,
        selectedData,
        allData,
        useImages,
        nowName,
        requestKey
      }
    }

    const result = mapAlbumContents(response.data.contents, type)

    selectedData = appendChildData(selectedData, row, result)
    allData = selectedData

    const images = result.filter(item => item.type != 'mp4')
    useImages = appendChildData(useImages, row, images)

    const nowData = allData.find(item => item.name === albumKey)
    const totalNum = nowData.list.length

    if (Number(totalNum) === Number(row.amount)) {
      foldersInfos.forEach((item, index) => {
        if (item.name === nowData.name) foldersInfos[index] = { ...item, maxNum: true }
      })
    }

    return {
      foldersInfos,
      fileRid,
      level,
      selectedData,
      allData,
      useImages,
      nowName,
      requestKey
    }
  }
)

/**
 * 派发相册详情请求，自动带上当前 deviceContext.requestKey。
 * @param {Object} args
 * @returns {Function}
 */
export const fetchPhotoData = args => (dispatch, getState) => {
  const requestKey = selectDeviceRequestKey(getState())

  return dispatch(fetchPhotoDataRequest({ ...args, requestKey }))
}

export const resetNowName = createAsyncThunk('resetNowName', async ({ fileRid, level, nowName }) => ({
  fileRid,
  level,
  nowName
}))

export const appChatSlice = createSlice({
  name: 'Photo Data',
  initialState: createInitialState(),
  reducers: {
    removeNowName: state => {
      state.level = 0
      state.nowName = ''
    }
  },
  extraReducers: builder => {
    addDeviceSwitchReset(builder, createInitialState)

    builder.addCase(fetchPhotoDataRequest.pending, (state, action) => {
      state.isLoading = true
      state.deviceRequestKey = action.meta.arg.requestKey || ''
      state.activePhotoRequestId = action.meta.requestId
    })
    builder.addCase(fetchPhotoDataRequest.fulfilled, (state, action) => {
      // 丢弃切换设备前发出的旧响应
      if (
        state.deviceRequestKey !== action.meta.arg.requestKey ||
        state.activePhotoRequestId !== action.meta.requestId
      ) {
        return
      }

      state.foldersInfos = [...action.payload.foldersInfos]
      state.fileRid = action.payload.fileRid
      state.level = action.payload.level
      state.selectedData = [...action.payload.selectedData]
      state.allData = [...action.payload.allData]
      state.useImages = [...action.payload.useImages]
      state.nowName = action.payload.nowName
      state.isLoading = false
    })
    builder.addCase(fetchPhotoDataRequest.rejected, (state, action) => {
      if (
        state.deviceRequestKey === action.meta.arg.requestKey &&
        state.activePhotoRequestId === action.meta.requestId
      ) {
        state.isLoading = false
      }
    })
    builder.addCase(resetNowName.fulfilled, (state, action) => {
      state.fileRid = action.payload.fileRid
      state.level = action.payload.level
      state.nowName = action.payload.nowName
    })
  }
})

export const { removeNowName } = appChatSlice.actions

export default appChatSlice.reducer
